import { z } from "zod";
import { honoursIdempotencyKey } from "./idempotency.js";
// What a caller holding an account API key may reach (V6 plan 13.3.1, row H14). Every
// procedure's `.meta()` declares `access`: "read" for what only reads (catalog, project and
// run reads, the stream, outputs and bytes) and "write" for what creates, changes, starts or
// spends. `keys: false` marks what no key may ever call, whatever its access. The server
// enforces both for a key caller in one middleware and one route check; a cookie caller is
// not affected. A procedure or route with no declaration is refused to keys (fail closed).
// The hand-written routes declare theirs in routes.ts (API_ROUTES).
export const ApiAccessSchema = z.enum(["read", "write"]);
/**
 * Every fact a procedure's `.meta()` may carry, held strictly by the contract test so a
 * misspelt key fails there rather than refusing keys at run time:
 * `public` = answers without a session, `idempotencyKey` = the Idempotency-Key header is
 * required and must equal the input's requestId, `sessionChanging` = mints or clears the
 * cookie, `access` = the level a key needs, `keys: false` = never callable with a key,
 * `replays: false` = a write whose answer is never stored for an Idempotency-Key replay
 * (idempotency.ts).
 */
export const ProcedureMetaSchema = z
    .object({
    public: z.literal(true).optional(),
    idempotencyKey: z.literal(true).optional(),
    sessionChanging: z.literal(true).optional(),
    access: ApiAccessSchema,
    keys: z.literal(false).optional(),
    replays: z.literal(false).optional(),
})
    .strict();
/** A procedure's declaration from its meta, or null when it declares no valid access. */
export function readAccessMeta(meta) {
    if (typeof meta !== "object" || meta === null)
        return null;
    const { access, keys, public: isPublic } = meta;
    const parsed = ApiAccessSchema.safeParse(access);
    if (!parsed.success)
        return null;
    return {
        access: parsed.data,
        ...(keys === false ? { keys: false } : {}),
        ...(isPublic === true ? { public: true } : {}),
    };
}
/**
 * The request header a write's retry carries (idempotency.ts, docs/HEADLESS.md "Retries"), and
 * the one a money command names its durable selection in.
 */
export const IDEMPOTENCY_KEY_HEADER = "Idempotency-Key";
/** Its longest value: a key is any string of 1 to 255 characters. */
export const IDEMPOTENCY_KEY_MAX_LENGTH = 255;
/** What the optional Idempotency-Key does on a write that honours it (H.5). */
export const IDEMPOTENCY_KEY_DESCRIPTION = "Optional, 1 to 255 characters (400 invalid_idempotency_key otherwise). The first call with a key runs and its answer is kept for 24 hours; a retry with the same key and the same request does nothing and answers that answer again, with Idempotency-Replayed: true. The same key with another request is 409 idempotency_key_reused, and while the first call still runs, 409 idempotency_in_flight. No header: every call runs.";
/**
 * The request headers a procedure's meta declares, for whoever describes the call (the OpenAPI
 * document's header parameters, generated from this): every write that honours an
 * Idempotency-Key (honoursIdempotencyKey: not one that mints or clears the cookie, not one that
 * declares `replays: false`) declares it optional; `idempotencyKey: true` makes it required and
 * equal to the input's requestId (billing.selectOffer). The one reader of these facts, so a
 * header declared another way later is a change to the meta and to this function, never to the
 * generator.
 */
export function readHeaderMeta(meta) {
    if (typeof meta !== "object" || meta === null)
        return [];
    const schema = { type: "string", maxLength: IDEMPOTENCY_KEY_MAX_LENGTH };
    if (meta.idempotencyKey === true)
        return [
            {
                name: IDEMPOTENCY_KEY_HEADER,
                required: true,
                description: "The durable selection's identity: required, and equal to the input's requestId (400 idempotency_key_required without it, idempotency_key_mismatch when it differs). A retry with it answers the first answer again, with Idempotency-Replayed: true.",
                schema,
            },
        ];
    if (!honoursIdempotencyKey(meta))
        return [];
    return [
        {
            name: IDEMPOTENCY_KEY_HEADER,
            required: false,
            description: IDEMPOTENCY_KEY_DESCRIPTION,
            schema,
        },
    ];
}
/** Whether a key granted `granted` may call what needs `needed`: write covers read. */
export function accessCovers(granted, needed) {
    return granted === "write" || needed === "read";
}
/**
 * The refusals a key caller can meet at the access check, each 403: the key's access does
 * not cover the procedure or route, or it is `keys: false`, or it declares no access
 * (`api_key_forbidden`); a key held to one project called something outside it, or
 * something account-wide (`api_key_project_scoped`).
 */
export const API_KEY_FORBIDDEN = "api_key_forbidden";
export const API_KEY_PROJECT_SCOPED = "api_key_project_scoped";
/**
 * The refusals a request presenting a key (`Authorization: Bearer v5k_…` or `X-Api-Key: v5k_…`)
 * can meet before any procedure or route: 401 `api_key_invalid` for a key that is unknown,
 * revoked or expired, is the server's own engine key, belongs to an account that is not active,
 * or is not a key at all; 403 `api_key_wrong_site` when the request's host is the other site's
 * (the key's site is the site, so any other host the app answers is accepted).
 */
export const API_KEY_INVALID = "api_key_invalid";
export const API_KEY_WRONG_SITE = "api_key_wrong_site";
/**
 * The admin surface is cookie-only (decision D70): a bearer session (`Authorization: Bearer
 * v5s_…`) resolves to the viewer the cookie does, a platform admin's included, yet every
 * procedure in these areas refuses it with 403 `admin_requires_cookie`, and identity.bootstrap
 * answers it `admin: null`. Key management is cookie-only too (D71): a stolen 24-hour bearer
 * must not mint a write key with no expiry, a lasting credential; keys are managed from the
 * site's own page. Keys never reach these areas either (`keys: false`).
 */
export const COOKIE_ONLY_AREAS = ["admin", "devtools", "apiKeys"];
export const ADMIN_REQUIRES_COOKIE = "admin_requires_cookie";
//# sourceMappingURL=access.js.map